import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, InputBase, Typography, keyframes } from '@mui/material';
import { THEME_COLOR } from '../../../theme/formStyles';
import { isOnDay, siteColor, stepProgressLabel, todayYmd } from '../projectSchedule.utils';
import { stepDate } from './ProjectStepper';

const CARDS_SHOWN = 8;

const blink = keyframes`50% { opacity: .25; }`;

// Status pill, as in the v69 design
const STATUS = {
    late: { label: 'Delayed', color: '#c62828', bgcolor: '#fdecec' },
    ok: { label: 'Completed', color: '#15803d', bgcolor: '#e8f7ee' },
    on: { label: 'In progress', color: THEME_COLOR, bgcolor: '#e6f4f2' },
    idle: { label: 'Not started', color: '#64748b', bgcolor: '#f1f5f9' },
};
const statusOf = (p) => (p.status === 'Delayed' ? STATUS.late : p.status === 'Completed' ? STATUS.ok : p.stage === 'Not started' ? STATUS.idle : STATUS.on);

// Progress ring with the % in the middle
function Ring({ pct, color }) {
    const r = 17;
    const c = 2 * Math.PI * r;
    return (
        <Box component="svg" viewBox="0 0 44 44" sx={{ width: 48, height: 48, flex: 'none' }}>
            <circle cx="22" cy="22" r={r} fill="none" stroke="#e8eeec" strokeWidth="5" />
            {pct > 0 && <circle cx="22" cy="22" r={r} fill="none" stroke={color} strokeWidth="5" strokeLinecap="round" strokeDasharray={`${(c * pct) / 100} ${c}`} transform="rotate(-90 22 22)" />}
            <text x="22" y="26" textAnchor="middle" style={{ fontSize: '10.5px', fontWeight: 700, fill: '#1f2933', fontFamily: 'inherit' }}>{pct}%</text>
        </Box>
    );
}

// The matched part of the text, marked
function Highlight({ text, query }) {
    const i = query ? text.toLowerCase().indexOf(query.toLowerCase()) : -1;
    if (i < 0) return text;
    return <>{text.slice(0, i)}<Box component="mark" sx={{ bgcolor: '#fff3b0', color: 'inherit', borderRadius: '3px', px: '1px' }}>{text.slice(i, i + query.length)}</Box>{text.slice(i + query.length)}</>;
}

const shortDate = (step) => { const d = stepDate(step); return d === 'Date TBD' ? 'TBD' : d; };

function ProjectCard({ project, query, todayTasks, onSelect }) {
    const color = siteColor(project.facility_id);
    const status = statusOf(project);
    const { current, next } = stepProgressLabel(project);
    const line = current ? current.label : next ? `Next: ${next.label}` : 'All steps done';
    const sub = current ? shortDate(current) : next ? shortDate(next) : '';
    return (
        <ButtonBase onClick={() => onSelect(project.id)} sx={{
            position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.5, textAlign: 'left', overflow: 'hidden',
            border: '1px solid #e5e9ee', bgcolor: '#fff', borderRadius: 4, p: '16px 14px 14px', transition: 'transform .12s, box-shadow .12s',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 22px rgba(16,40,32,.1)' },
        }}>
            <Box sx={{ position: 'absolute', left: 0, right: 0, top: 0, height: 46, background: `linear-gradient(135deg, ${color}, transparent 140%)`, opacity: 0.14 }} />
            <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
                <Ring pct={project.progress} color={color} />
                <Box component="span" sx={{ fontSize: '11px', fontWeight: 700, borderRadius: 99, px: '9px', py: '3px', whiteSpace: 'nowrap', color: status.color, bgcolor: status.bgcolor }}>{status.label}</Box>
            </Box>
            <Typography noWrap sx={{ fontSize: '15px', fontWeight: 600 }}>
                <Highlight text={project.facility_name} query={query} />
                {project.description && <> · <Highlight text={project.description} query={query} /></>}
            </Typography>
            <Typography noWrap sx={{ fontSize: '12px', color: '#6b7785' }}>{project.location_name || project.project_type || ' '}</Typography>
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', mt: 1, pt: 1, borderTop: '1px dashed #e5e9ee', fontSize: '12.5px', color: '#334155', minHeight: 34 }}>
                {todayTasks.length ? (
                    <>
                        <Box component="i" sx={{ fontStyle: 'normal', color: '#16a34a', fontSize: '9px', mt: '3px', animation: `${blink} 1.4s infinite` }}>●</Box>
                        <Box component="span" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            <b style={{ fontWeight: 600 }}>{todayTasks.length} task{todayTasks.length > 1 ? 's' : ''} today</b>
                            <em style={{ fontStyle: 'normal', color: '#6b7785' }}> · {todayTasks.map(t => t.sub_task_name).join(' · ')}</em>
                        </Box>
                    </>
                ) : (
                    <>
                        <Box component="i" sx={{ fontStyle: 'normal', color, fontSize: '9px', mt: '3px' }}>▶</Box>
                        <span>{line}{sub && <em style={{ fontStyle: 'normal', color: '#6b7785' }}> · {sub}</em>}</span>
                    </>
                )}
            </Box>
        </ButtonBase>
    );
}

// Search under the portfolio cards when "All projects" is picked. As in the v69 design, project cards appear
// only while searching (up to 8 matches); a project with work today shows "● N tasks today" instead of its step.
// schedule = the sub-tasks loaded for the calendar (today's work is found there when the current month is shown).
export default function ProjectOverviewCards({ projects, schedule = [], onSelect }) {
    const [query, setQuery] = useState('');
    const q = query.trim().toLowerCase();
    const today = todayYmd();

    const list = useMemo(
        () => (q ? projects.filter(p => `${p.facility_name} ${p.description || ''} ${p.location_name || ''}`.toLowerCase().includes(q)) : []),
        [projects, q]
    );
    const todayByProject = useMemo(() => {
        const map = new Map();
        schedule.filter(t => t.project_id && isOnDay(t, today)).forEach(t => map.set(t.project_id, [...(map.get(t.project_id) || []), t]));
        return map;
    }, [schedule, today]);

    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid #e5e9ee', borderRadius: '10px', px: 1.5, py: 1, bgcolor: '#fff' }}>
                <span>🔍</span>
                <InputBase value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${projects.length} projects by name or city`}
                    inputProps={{ autoComplete: 'off', 'aria-label': 'Search projects' }} sx={{ flex: 1, minWidth: 0, fontSize: { xs: '16px', sm: '14px' } }} />
                {query && (
                    <Box component="button" type="button" aria-label="Clear" onClick={() => setQuery('')}
                        sx={{ border: 0, bgcolor: '#f4f6f8', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', color: '#6b7785', p: 0 }}>×</Box>
                )}
            </Box>
            {q && (
                <>
                    <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: '#6b7785', textTransform: 'uppercase', letterSpacing: '.05em', m: '16px 2px 10px' }}>
                        {list.length} match{list.length === 1 ? '' : 'es'}
                    </Typography>
                    {list.length ? (
                        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', lg: 'repeat(4, minmax(0,1fr))' } }}>
                            {list.slice(0, CARDS_SHOWN).map(p => (
                                <ProjectCard key={p.id} project={p} query={query.trim()} todayTasks={todayByProject.get(p.id) || []} onSelect={onSelect} />
                            ))}
                        </Box>
                    ) : (
                        <Typography sx={{ textAlign: 'center', color: '#6b7785', fontSize: '13px', py: 2 }}>No project matches.</Typography>
                    )}
                    {list.length > CARDS_SHOWN && (
                        <Typography sx={{ textAlign: 'center', fontSize: '12px', color: '#6b7785', mt: 1 }}>+{list.length - CARDS_SHOWN} more · keep typing</Typography>
                    )}
                </>
            )}
        </Box>
    );
}
